import {
  Button,
  InputGroup,
  InputGroupProps,
  Menu,
  MenuDivider,
  MenuItem,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import {
  ItemListPredicate,
  ItemListRenderer,
  ItemListRendererProps,
  ItemPredicate,
  ItemRenderer,
  ItemRendererProps,
  QueryList,
  QueryListProps,
  QueryListRendererProps,
  renderFilteredItems,
} from '@blueprintjs/select';
import classNames from 'classnames';
import debounce from 'lodash.debounce';
import * as React from 'react';
import {
  SELECT_ITEM_DIVIDER_VALUE,
  SelectItem,
  selectItemPredicate,
} from 'vet-bones/utils/select';

import { TEST_IDS } from 'src/constants/testIds';
import * as copy from 'src/templates/copy';

type ItemQueryListProps<ItemType> = {
  autofocus?: boolean;
  className?: string;
  collapsibleInput?: boolean;
  customRenderer?: (
    listProps: QueryListRendererProps<ItemType>,
    queryListInput: JSX.Element,
    isInputCollapsed?: boolean
  ) => JSX.Element;
  debounceOnInputChange?: boolean | number; // boolean uses default interval; non-zero number sets custom interval
  disabled?: boolean;
  inputProps?: InputGroupProps & { testId?: string };
  itemPredicate?: ItemPredicate<ItemType>;
  itemRenderer?: ItemRenderer<ItemType>;
  itemListPredicate?: ItemListPredicate<ItemType>;
  itemListRenderer?: ItemListRenderer<ItemType>;
  items: ItemType[];
  noResults?: string;
  onQueryChange?: QueryListProps<ItemType>['onQueryChange'];
  onSelect: QueryListRendererProps<ItemType>['handleItemSelect'];
};

const DEFAULT_DEBOUNCE_MS = 50;

export class ItemQueryList<ItemType> extends React.Component<
  ItemQueryListProps<ItemType>,
  { isInputCollapsed: boolean }
> {
  public static ofType<U>() {
    return ItemQueryList as new (
      props: ItemQueryListProps<U>
    ) => ItemQueryList<U>;
  }

  private listRef = React.createRef<QueryList<ItemType>>();

  private inputRef = React.createRef<HTMLInputElement>();

  private debouncedOnInputChangeRef = React.createRef<
    (value: string) => void
  >() as React.MutableRefObject<(value: string) => void>;

  public constructor(props: ItemQueryListProps<ItemType>) {
    super(props);
    this.state = { isInputCollapsed: Boolean(props.collapsibleInput) };
  }

  private resetQuery = () => {
    const { collapsibleInput, onQueryChange } = this.props;
    if (collapsibleInput) {
      this.setState({ isInputCollapsed: true });
    }
    this.listRef.current?.setQuery('', true);
    if (this.inputRef.current) {
      this.inputRef.current.value = '';
    }
    if (onQueryChange) {
      onQueryChange('');
    }
  };

  public render() {
    const {
      autofocus = true,
      className,
      customRenderer,
      debounceOnInputChange,
      disabled,
      inputProps,
      collapsibleInput,
      itemRenderer = (item, { handleClick, modifiers }: ItemRendererProps) => {
        if (!modifiers.matchesPredicate) {
          return null;
        }
        // If no `itemRenderer` is provided, we assume the caller is working
        // with simple `SelectItem` items
        const selectItem = item as unknown as SelectItem;
        return selectItem.value === SELECT_ITEM_DIVIDER_VALUE ? (
          <MenuDivider key={selectItem.text} title={selectItem.text} />
        ) : (
          <MenuItem
            key={selectItem.value}
            onClick={handleClick}
            text={selectItem.text}
          />
        );
      },
      itemListPredicate,
      itemListRenderer = (listProps: ItemListRendererProps<ItemType>) => (
        <Menu
          className={classNames('sd-query-list', className)}
          ulRef={listProps.itemsParentRef}
        >
          {renderFilteredItems(listProps, noResults)}
        </Menu>
      ),
      items,
      // Again, if no `itemPredicate` is provided, we assume the caller is
      // working with simple `SelectItem` items
      itemPredicate = selectItemPredicate()(
        items as unknown as SelectItem[]
      ) as unknown as ItemPredicate<ItemType>,
      noResults = copy.components.defaults.noResults,
      onQueryChange,
      onSelect,
    } = this.props;

    const { isInputCollapsed } = this.state;

    return (
      <QueryList
        ref={this.listRef}
        disabled={disabled}
        itemListPredicate={itemListPredicate}
        itemListRenderer={itemListRenderer}
        itemPredicate={itemPredicate}
        itemRenderer={itemRenderer}
        items={items}
        noResults={noResults}
        onItemSelect={onSelect}
        onQueryChange={debounceOnInputChange ? undefined : onQueryChange}
        renderer={(listProps) => {
          const queryListInput = isInputCollapsed ? (
            <Button
              data-testid={TEST_IDS.itemQueryList.filterButton}
              icon={inputProps?.leftIcon || IconNames.SEARCH}
              minimal
              onClick={() => {
                this.setState({ isInputCollapsed: false });
              }}
            />
          ) : (
            // Note: this `InputGroup` is uncontrolled so that filtering can be
            // debounced if necessary. We do not want the value of the input itself
            // to display in a debounced manner, so we leave its value uncontrolled.
            // This is also why we keep a `ref` for the underlying input component,
            // so that we can still clear its value on reset.
            <InputGroup
              autoFocus={autofocus}
              className={inputProps?.className}
              data-testid={inputProps?.testId}
              inputRef={this.inputRef}
              leftIcon={inputProps?.leftIcon || IconNames.SEARCH}
              onBlur={(event) => {
                const { value } = event.currentTarget;
                if (!value) {
                  this.resetQuery();
                }
              }}
              onChange={
                debounceOnInputChange
                  ? (event) => {
                      const { value } = event.currentTarget;

                      if (!this.debouncedOnInputChangeRef.current) {
                        this.debouncedOnInputChangeRef.current = debounce(
                          (value: string) => {
                            // Trigger filtering only after interval has passed.
                            this.listRef.current?.setQuery(value);

                            // Deconstruct this.props inside debounced function to ensure current refs
                            const { onQueryChange } = this.props;
                            if (onQueryChange) {
                              onQueryChange(value);
                            }
                          },
                          typeof debounceOnInputChange === 'number'
                            ? debounceOnInputChange
                            : DEFAULT_DEBOUNCE_MS
                        );
                      }

                      this.debouncedOnInputChangeRef.current(value);
                    }
                  : listProps.handleQueryChange
              }
              placeholder={
                inputProps?.placeholder || copy.components.defaults.filter
              }
              rightElement={
                // Show the X if there's a query OR always show it if collapsible
                listProps.query.length > 0 || collapsibleInput ? (
                  <Button
                    icon={IconNames.CROSS}
                    minimal
                    onClick={this.resetQuery}
                    // Prevent clicks from stealing focus:
                    onMouseDown={(event) => event.preventDefault()}
                  />
                ) : undefined
              }
            />
          );
          return customRenderer ? (
            customRenderer(listProps, queryListInput, isInputCollapsed)
          ) : (
            <>
              {queryListInput}
              {listProps.itemList}
            </>
          );
        }}
      />
    );
  }
}
